/**
 * Tooltip — branded, colorful hover bubble.
 *
 * On touch devices (`@media (hover: none)`) the wrapper is a
 * pass-through that just renders `children`. This prevents the
 * frustrating tap-shows-tooltip-then-tap-again behavior on phones
 * and tablets, especially for action buttons like the language
 * switcher where the first tap should fire the click immediately.
 *
 * Why this exists:
 *   Native browser `title=` tooltips look unbranded and inconsistent.
 *   This component renders a small, professional pill-style tooltip
 *   that matches the rest of the site palette (purple / blue / green
 *   accents) with a smooth fade-and-slide-up animation and an arrow.
 *
 * Usage:
 *   <Tooltip label="Filter to JapanETCcard" accent="purple">
 *     <button>JEC</button>
 *   </Tooltip>
 *
 * Notes:
 *   - CSS-only show/hide via `group-hover` / `group-focus-within` so it
 *     stays cheap and accessible (no portal, no JS positioning).
 *   - The trigger element keeps its own focus + interaction behavior.
 *   - `placement` defaults to `top` — covers ~95% of trigger contexts.
 */
import React, { useEffect, useState } from "react";

const ACCENTS = {
  purple: {
    bg: "#663399",      // logo purple
    text: "#FFFFFF",
    arrow: "#663399",
  },
  blue: {
    bg: "#2563EB",
    text: "#FFFFFF",
    arrow: "#2563EB",
  },
  green: {
    bg: "#16A34A",
    text: "#FFFFFF",
    arrow: "#16A34A",
  },
  indigo: {
    bg: "#4F46E5",
    text: "#FFFFFF",
    arrow: "#4F46E5",
  },
  pink: {
    bg: "#D946EF",
    text: "#FFFFFF",
    arrow: "#D946EF",
  },
  sky: {
    bg: "#0EA5E9",
    text: "#FFFFFF",
    arrow: "#0EA5E9",
  },
  amber: {
    bg: "#F59E0B",
    text: "#FFFFFF",
    arrow: "#F59E0B",
  },
  rose: {
    bg: "#E11D48",
    text: "#FFFFFF",
    arrow: "#E11D48",
  },
  dark: {
    bg: "#0F1D2D",
    text: "#FFFFFF",
    arrow: "#0F1D2D",
  },
};

export default function Tooltip({
  label,
  accent = "dark",
  placement = "top",
  delay = 150,
  children,
  testId,
}) {
  const a = ACCENTS[accent] || ACCENTS.dark;

  // Detect whether the device supports true hover. Touch-only devices
  // (phones / most tablets) report `(hover: none)`. On those devices
  // the tooltip would intercept the first tap and require a second tap
  // to actually fire the underlying button's onClick — extremely
  // frustrating UX. So we skip the tooltip entirely there.
  //
  // Default state is `false` (assume touch-first) so the very first
  // React render on mobile renders the children DIRECTLY with no
  // wrapper. The useEffect below only promotes to `true` on devices
  // that genuinely support hover. This avoids the tooltip wrapper
  // briefly being present during hydration, which on iOS Safari and
  // Android Chrome can swallow the first tap on the underlying
  // button (the user then has to tap 2-3 times to fire the click).
  const [hoverable, setHoverable] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setHoverable(mql.matches);
    update();
    if (mql.addEventListener) mql.addEventListener("change", update);
    else mql.addListener(update);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", update);
      else mql.removeListener(update);
    };
  }, []);

  if (!label || !hoverable) return children;

  const positionStyles =
    placement === "bottom"
      ? "top-full mt-2"
      : placement === "left"
      ? "right-full mr-2 top-1/2 -translate-y-1/2"
      : placement === "right"
      ? "left-full ml-2 top-1/2 -translate-y-1/2"
      : "bottom-full mb-2";

  const transformOrigin =
    placement === "bottom"
      ? "origin-top"
      : placement === "left"
      ? "origin-right"
      : placement === "right"
      ? "origin-left"
      : "origin-bottom";

  // Tail (arrow) position — appears on the side of the bubble that
  // faces the trigger.
  const arrowStyles = {
    top: {
      className: "left-1/2 -translate-x-1/2 -bottom-1",
      transform: "rotate(45deg)",
    },
    bottom: {
      className: "left-1/2 -translate-x-1/2 -top-1",
      transform: "rotate(45deg)",
    },
    left: {
      className: "top-1/2 -translate-y-1/2 -right-1",
      transform: "rotate(45deg)",
    },
    right: {
      className: "top-1/2 -translate-y-1/2 -left-1",
      transform: "rotate(45deg)",
    },
  }[placement] || { className: "left-1/2 -translate-x-1/2 -bottom-1", transform: "rotate(45deg)" };

  return (
    <span className="group/tt relative inline-flex">
      {children}
      <span
        role="tooltip"
        aria-hidden="true"
        data-testid={testId}
        className={[
          "pointer-events-none absolute z-50 left-1/2 -translate-x-1/2",
          positionStyles,
          transformOrigin,
          "whitespace-nowrap rounded-full px-3 py-1.5 text-[11.5px] font-semibold tracking-tight",
          "shadow-[0_10px_24px_-10px_rgba(15,29,45,0.45)]",
          "opacity-0 scale-95 translate-y-1",
          "group-hover/tt:opacity-100 group-hover/tt:scale-100 group-hover/tt:translate-y-0",
          "group-focus-within/tt:opacity-100 group-focus-within/tt:scale-100 group-focus-within/tt:translate-y-0",
          "transition-all duration-200 ease-out",
        ].join(" ")}
        style={{
          backgroundColor: a.bg,
          color: a.text,
          transitionDelay: `${delay}ms`,
        }}
      >
        {label}
        {/* Arrow tail */}
        <span
          aria-hidden
          className={`absolute w-2 h-2 ${arrowStyles.className}`}
          style={{ backgroundColor: a.arrow, transform: arrowStyles.transform }}
        />
      </span>
    </span>
  );
}
